/**
 * iter201 — useSyncReload(reload)
 *
 * Calls `reload()` when:
 *   • The Service Worker posts SYNC_COMPLETE (Background Sync drained
 *     the queue while the page may have been backgrounded).
 *   • The page-side durableQueue empties out (the in-app retry loop
 *     successfully replayed queued writes).
 *
 * Components mount this with their own `fetchGroup` / `fetchExpenses`
 * callback. After a write completes offline → comes back online →
 * syncs → the UI auto-refreshes so the user sees the just-saved data
 * without manually pulling-to-refresh.
 */
import { useEffect, useRef } from 'react';
import { onChange as onQueueChange, getPending } from '../lib/durableQueue';

export default function useSyncReload(reload) {
  const lastSizeRef = useRef(0);

  useEffect(() => {
    if (typeof reload !== 'function') return undefined;

    // Track current queue size (best-effort, async)
    getPending().then((items) => { lastSizeRef.current = items.length; }).catch(() => {});

    // 1) Service worker → background sync drained queue
    const onSwMessage = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE' && event.data.count > 0) {
        reload();
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);

    // 2) In-app retry loop drained the queue (durableQueue emits a
    //    change with no args; we re-read the size to decide)
    const unsubscribe = onQueueChange(async () => {
      try {
        const items = await getPending();
        const next = items.length;
        if (lastSizeRef.current > 0 && next === 0) {
          reload();
        }
        lastSizeRef.current = next;
      } catch { /* noop */ }
    });

    return () => {
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
      try { unsubscribe(); } catch { /* noop */ }
    };
  }, [reload]);
}
